import { useState } from 'react';
import type { FormEvent } from 'react';
import type { InviteRole } from '@dental-staffing-platform/shared';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Pagination,
  Row,
  Select,
  Spinner,
  Stack,
} from '@dental-staffing-platform/ui';
import { adminApi, type InviteRow } from '../lib/admin-client.js';
import { humanize } from '../lib/format.js';
import { useAsync } from '../lib/useAsync.js';

const ROLES: { value: InviteRole; label: string }[] = [
  { value: 'professional', label: 'Professional' },
  { value: 'group_admin', label: 'Group admin' },
  { value: 'location_manager', label: 'Location manager' },
  { value: 'credential_reviewer', label: 'Credential reviewer' },
];

/** Public app origin for absolute accept links copied from admin. */
const PUBLIC_ORIGIN = 'https://dental-staffing-public.pages.dev';

function absoluteAcceptUrl(path: string | undefined): string | null {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${PUBLIC_ORIGIN}${path.startsWith('/') ? path : `/${path}`}`;
}

/** Admin console invites oversight. */
export function Invites() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [roleFilter, setRoleFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<InviteRole>('professional');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const list = useAsync(
    () =>
      adminApi.invitations.page({
        page,
        pageSize: 20,
        ...(statusFilter !== 'all' ? { status: statusFilter as InviteRow['status'] } : {}),
        ...(roleFilter !== 'all' ? { role: roleFilter as InviteRole } : {}),
        ...(search.trim() ? { search: search.trim() } : {}),
      }),
    [page, statusFilter, roleFilter, search]
  );

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const created = await adminApi.invitations.create({
        email: email.trim(),
        role,
        scopeType: 'group',
        ...(firstName.trim() ? { firstName: firstName.trim() } : {}),
        ...(lastName.trim() ? { lastName: lastName.trim() } : {}),
      });
      const url = absoluteAcceptUrl(created.acceptPath);
      setInfo(created.message ?? (url ? `Invite created. Accept link: ${url}` : 'Invite created.'));
      if (url) {
        try {
          await navigator.clipboard.writeText(url);
          setCopiedId(created.id);
        } catch {
          // ignore
        }
      }
      setEmail('');
      setFirstName('');
      setLastName('');
      list.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send invite');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id: string) => {
    setError(null);
    try {
      await adminApi.invitations.revoke(id);
      list.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not revoke');
    }
  };

  const copyLink = async (inv: InviteRow) => {
    const url = absoluteAcceptUrl(inv.acceptPath);
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(inv.id);
    } catch {
      setError('Could not copy link');
    }
  };

  return (
    <div>
      <PageHeader title="Invites" subtitle="Invite users into this organization" />
      <Card style={{ marginBottom: 16 }}>
        <form onSubmit={(e) => void submit(e)}>
          <Stack gap={12}>
            <Row gap={12} style={{ alignItems: 'flex-start' }}>
              <Field label="Email">
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="colleague@practice.com"
                />
              </Field>
              <Field label="Role">
                <Select value={role} onChange={(e) => setRole(e.target.value as InviteRole)}>
                  {ROLES.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </Select>
              </Field>
            </Row>
            <Row gap={12} style={{ alignItems: 'flex-start' }}>
              <Field label="First name (optional)">
                <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              </Field>
              <Field label="Last name (optional)">
                <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </Field>
            </Row>
            {error ? <span className="dsp-field__error">{error}</span> : null}
            {info ? <span className="dsp-muted">{info}</span> : null}
            <Button type="submit" disabled={busy}>
              {busy ? 'Sending…' : 'Send invite'}
            </Button>
          </Stack>
        </form>
      </Card>

      <Card style={{ marginBottom: 16 }}>
        <Row gap={12} style={{ alignItems: 'end', flexWrap: 'wrap' }}>
          <Field label="Search invitations">
            <Input
              type="search"
              value={search}
              placeholder="Email address"
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </Field>
          <Field label="Status">
            <Select
              value={statusFilter}
              onChange={(event) => {
                setStatusFilter(event.target.value);
                setPage(1);
              }}
            >
              <option value="all">All statuses</option>
              <option value="pending">Pending</option>
              <option value="accepted">Accepted</option>
              <option value="revoked">Revoked</option>
              <option value="expired">Expired</option>
            </Select>
          </Field>
          <Field label="Role">
            <Select
              value={roleFilter}
              onChange={(event) => {
                setRoleFilter(event.target.value);
                setPage(1);
              }}
            >
              <option value="all">All roles</option>
              {ROLES.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </Select>
          </Field>
        </Row>
      </Card>

      {list.loading ? (
        <Spinner label="Loading invites…" />
      ) : list.error ? (
        <Card>
          <span className="dsp-field__error">{list.error}</span>
        </Card>
      ) : !list.data || list.data.items.length === 0 ? (
        <EmptyState
          title="No matching invites"
          description="Adjust the filters or send a new invitation."
        />
      ) : (
        <Stack gap={10}>
          {list.data.items.map((inv) => (
            <Card key={inv.id}>
              <Row style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{inv.email}</div>
                  <div className="dsp-muted" style={{ fontSize: '0.9rem' }}>
                    {humanize(inv.role)} · expires {new Date(inv.expiresAt).toLocaleDateString()}
                  </div>
                </div>
                <Row gap={8}>
                  <Badge tone={inv.expired || inv.status === 'expired' ? 'neutral' : 'warning'}>
                    {inv.expired || inv.status === 'expired' ? 'Expired' : 'Pending'}
                  </Badge>
                  {inv.acceptPath && !(inv.expired || inv.status === 'expired') ? (
                    <Button variant="secondary" size="sm" onClick={() => void copyLink(inv)}>
                      {copiedId === inv.id ? 'Copied' : 'Copy link'}
                    </Button>
                  ) : null}
                  {!(inv.expired || inv.status === 'expired') ? (
                    <Button variant="secondary" size="sm" onClick={() => void revoke(inv.id)}>
                      Revoke
                    </Button>
                  ) : null}
                </Row>
              </Row>
            </Card>
          ))}
          <Pagination
            {...list.data}
            itemLabel="invitations"
            disabled={list.loading}
            onPageChange={setPage}
          />
        </Stack>
      )}
    </div>
  );
}
